import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Shield, ArrowLeft, Search, UserX, UserCheck, Key, Trash2, Mail, Calendar, Package, Eye, Loader2, MoreVertical, ArrowRightLeft, ShieldAlert, ShoppingCart, Trash, ChevronDown, ChevronUp, Crown, Download, Pencil, Phone, MapPin, Save, Clock } from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  Active: "status-active",
  Stolen: "status-stolen",
  Sold: "status-sold",
  Discarded: "status-discarded"
};

const STATUS_ICONS = {
  Active: Package,
  Stolen: ShieldAlert,
  Sold: ShoppingCart,
  Discarded: Trash
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  
  // User tools state
  const [expandedUser, setExpandedUser] = useState(null);
  const [userTools, setUserTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  
  // Tool action state
  const [selectedTool, setSelectedTool] = useState(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showToolDeleteDialog, setShowToolDeleteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState("");
  const [newStatus, setNewStatus] = useState("");

  // Edit user state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingUser, setSavingUser] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, statusFilter]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter) params.append("status", statusFilter);

      const response = await fetch(`${API}/admin/users?${params}`, { headers: getAuthHeaders() });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data.users);
      setTotal(data.total);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data);
        setShowUserDetail(true);
      }
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const handleSuspend = async (userId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/suspend`, { method: "PUT", headers: getAuthHeaders() });
      if (response.ok) {
        toast.success("User suspended");
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed");
      }
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async (userId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/unsuspend`, { method: "PUT", headers: getAuthHeaders() });
      if (response.ok) {
        toast.success("User unsuspended");
        fetchUsers();
      }
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/reset-password`, { method: "PUT", headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setTempPassword(data.temp_password);
        toast.success("Password reset successfully");
      }
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}`, { method: "DELETE", headers: getAuthHeaders() });
      if (response.ok) {
        toast.success("User deleted");
        setShowDeleteDialog(false);
        setShowUserDetail(false);
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed");
      }
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const fetchUserTools = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserTools([]);
      return;
    }
    
    setLoadingTools(true);
    setExpandedUser(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}/tools`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setUserTools(data.tools);
      }
    } catch (error) {
      toast.error("Failed to load user tools");
    } finally {
      setLoadingTools(false);
    }
  };

  const handleToolTransfer = async () => {
    if (!selectedTool || !newOwnerId) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API}/admin/tools/${selectedTool.tool_id}/transfer?new_owner_id=${newOwnerId}`,
        { method: "PUT", headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success("Tool transferred successfully");
        setShowTransferDialog(false);
        setNewOwnerId("");
        if (expandedUser) fetchUserTools(expandedUser);
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Transfer failed");
      }
    } catch (error) {
      toast.error("Transfer failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToolStatusChange = async () => {
    if (!selectedTool || !newStatus) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API}/admin/tools/${selectedTool.tool_id}/status?status=${newStatus}`,
        { method: "PUT", headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success(`Status changed to ${newStatus}`);
        setShowStatusDialog(false);
        setNewStatus("");
        if (expandedUser) fetchUserTools(expandedUser);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Status change failed");
      }
    } catch (error) {
      toast.error("Status change failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToolDelete = async () => {
    if (!selectedTool) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/tools/${selectedTool.tool_id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success("Tool deleted");
        setShowToolDeleteDialog(false);
        if (expandedUser) fetchUserTools(expandedUser);
        fetchUsers();
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setSavingUser(true);
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("User updated");
        setSelectedUser({ ...selectedUser, ...data.user });
        setEditMode(false);
        fetchUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setSavingUser(false);
    }
  };

  const handleCombinedExport = async () => {
    try {
      const response = await fetch(`${API}/admin/export/combined`, { headers: getAuthHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `toolio_combined_export_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Combined export downloaded");
      } else {
        toast.error("Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const startEditUser = () => {
    setEditForm({
      name: selectedUser?.name || "",
      email: selectedUser?.email || "",
      phone: selectedUser?.phone || "",
      postcode: selectedUser?.postcode || ""
    });
    setEditMode(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 px-4 py-3 lg:px-8">
          <Link to="/admin">
            <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Admin</Button>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-heading text-lg font-bold uppercase">User Management</span>
          </div>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={handleCombinedExport} data-testid="combined-export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export All Data
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-card border-input" data-testid="user-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-card" data-testid="user-status-filter">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="unverified">Unverified</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <p className="text-muted-foreground text-sm mb-4">Showing {users.length} of {total} users</p>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center"><p className="text-muted-foreground">No users found</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <Card key={user.user_id} className="border-border bg-card" data-testid={`user-card-${user.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* User Info - Clickable */}
                    <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => fetchUserTools(user.user_id)}>
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 relative">
                        <span className="text-primary font-bold">{user.name?.charAt(0)?.toUpperCase() || "U"}</span>
                        {user.is_admin && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center" title="Admin">
                            <Crown className="w-3 h-3 text-black" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{user.name}</p>
                          {user.is_admin && <Badge className="text-xs bg-yellow-500 text-black">Admin</Badge>}
                          {user.is_suspended && <Badge variant="destructive" className="text-xs">Suspended</Badge>}
                          {!user.email_verified && <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500">Unverified</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{user.email}</span></p>
                        <p className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {user.tool_count || 0} tools</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(user.created_at).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1" title={user.last_login ? new Date(user.last_login).toLocaleString("en-GB") : "Never"}>
                            <Clock className="w-3 h-3" /> {user.last_login ? `Last in ${new Date(user.last_login).toLocaleDateString("en-GB")}` : "Never logged in"}
                          </span>
                        </p>
                      </div>
                      {/* Expand chevron - visible on larger screens inline */}
                      <div className="hidden sm:flex items-center">
                        {expandedUser === user.user_id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons - Stack on mobile, inline on desktop */}
                    <div className="flex items-center justify-between sm:justify-end gap-1 border-t sm:border-t-0 pt-2 sm:pt-0 mt-1 sm:mt-0">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); fetchUserDetail(user.user_id); }} title="View Details"><Eye className="w-4 h-4" /></Button>
                        {user.is_suspended ? (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleUnsuspend(user.user_id); }} title="Unsuspend"><UserCheck className="w-4 h-4" /></Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleSuspend(user.user_id); }} title="Suspend"><UserX className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleResetPassword(user.user_id); }} title="Reset Password"><Key className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={(e) => { e.stopPropagation(); setSelectedUser(user); setShowDeleteDialog(true); }} title="Delete User"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      {/* Expand chevron - visible on mobile only */}
                      <div className="flex sm:hidden items-center" onClick={() => fetchUserTools(user.user_id)}>
                        {expandedUser === user.user_id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Tools Section */}
                  {expandedUser === user.user_id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="font-heading text-sm uppercase text-muted-foreground mb-3">
                        {user.name}'s Tools ({userTools.length})
                      </h4>
                      {loadingTools ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      ) : userTools.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No tools registered</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {userTools.map((tool) => {
                            const StatusIcon = STATUS_ICONS[tool.status] || Package;
                            const API_BASE = process.env.REACT_APP_BACKEND_URL;
                            const imageUrl = tool.image_url 
                              ? (tool.image_url.startsWith('http') ? tool.image_url : API_BASE + tool.image_url)
                              : null;
                            return (
                              <div 
                                key={tool.tool_id} 
                                className="p-3 bg-secondary/20 rounded-lg border border-border cursor-pointer hover:border-primary/50 transition-colors"
                                onClick={() => navigate(`/tool/${tool.tool_id}`, { state: { from: 'admin-users' } })}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <Badge className={`${STATUS_STYLES[tool.status]} border font-mono text-xs uppercase`}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {tool.status}
                                  </Badge>
                                  <div className="flex items-center gap-2">
                                    {/* Tool Image Thumbnail */}
                                    {imageUrl && (
                                      <div className="w-10 h-10 rounded overflow-hidden bg-black/20 flex-shrink-0">
                                        <img 
                                          src={imageUrl} 
                                          alt={tool.manufacturer}
                                          className="w-full h-full object-cover"
                                          onError={(e) => e.target.style.display = 'none'}
                                        />
                                      </div>
                                    )}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                          <MoreVertical className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTool(tool);
                                          setShowStatusDialog(true);
                                        }}>
                                          <Package className="w-4 h-4 mr-2" /> Change Status
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTool(tool);
                                          setShowTransferDialog(true);
                                        }}>
                                          <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Ownership
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                          className="text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTool(tool);
                                            setShowToolDeleteDialog(true);
                                          }}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" /> Delete Tool
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                                <h5 className="font-heading font-bold uppercase text-sm">{tool.manufacturer}</h5>
                                <p className="text-xs text-muted-foreground">{tool.tool_type}</p>
                                <p className="text-xs text-muted-foreground font-mono mt-1">S/N: {tool.serial_number}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showUserDetail} onOpenChange={(open) => { setShowUserDetail(open); if (!open) setEditMode(false); }}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading uppercase">User Details</DialogTitle>
                {selectedUser && !editMode && (
                  <Button variant="outline" size="sm" onClick={startEditUser} data-testid="edit-user-btn">
                    <Pencil className="w-3 h-3 mr-2" /> Edit
                  </Button>
                )}
              </div>
            </DialogHeader>
            {selectedUser && !editMode && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Name</p><p className="font-medium">{selectedUser.name}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Email</p><p className="font-medium">{selectedUser.email}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Phone</p><p className="font-medium">{selectedUser.phone || "—"}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Postcode</p><p className="font-medium">{selectedUser.postcode || "—"}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">User ID</p><p className="font-mono text-sm">{selectedUser.user_id}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Status</p><p className="font-medium">{selectedUser.is_suspended ? <span className="text-red-400">Suspended</span> : selectedUser.email_verified ? <span className="text-green-400">Active</span> : <span className="text-yellow-400">Unverified</span>}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Tools</p><p className="font-medium">{selectedUser.tool_count}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Sessions</p><p className="font-medium">{selectedUser.active_sessions}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Last Login</p><p className="font-medium text-sm">{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "Never"}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Created</p><p className="font-medium text-sm">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString("en-GB") : "—"}</p></div>
                  <div className="p-3 bg-secondary/20 rounded"><p className="text-xs text-muted-foreground uppercase">Auth Provider</p><p className="font-medium">{selectedUser.auth_provider || "email"}</p></div>
                </div>
                {tempPassword && (
                  <div className="p-4 bg-primary/10 border border-primary/50 rounded">
                    <p className="text-sm font-medium mb-2">Temporary Password</p>
                    <p className="font-mono text-lg text-primary">{tempPassword}</p>
                  </div>
                )}
              </div>
            )}
            {selectedUser && editMode && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase block mb-1">Name</label>
                    <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-background" data-testid="edit-user-name" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase block mb-1">Email</label>
                    <Input value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="bg-background" data-testid="edit-user-email" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase block mb-1">Phone</label>
                    <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="bg-background" data-testid="edit-user-phone" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase block mb-1">Postcode</label>
                    <Input value={editForm.postcode} onChange={(e) => setEditForm({...editForm, postcode: e.target.value})} className="bg-background" data-testid="edit-user-postcode" />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>User ID: <span className="font-mono">{selectedUser.user_id}</span></span>
                  <span className="mx-2">|</span>
                  <span>Created: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString("en-GB") : "—"}</span>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button onClick={handleSaveUser} disabled={savingUser} data-testid="save-user-btn">
                    {savingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading uppercase text-destructive">Delete User</DialogTitle><DialogDescription>Delete {selectedUser?.name}? This will remove all their tools too.</DialogDescription></DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={actionLoading} data-testid="confirm-delete-user-btn">{actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tool Transfer Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Transfer Tool Ownership</DialogTitle>
              <DialogDescription>
                Transfer <strong>{selectedTool?.manufacturer} {selectedTool?.tool_type}</strong> to another user.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm text-muted-foreground mb-2 block">New Owner User ID</label>
              <Input
                placeholder="e.g., user_abc123def456"
                value={newOwnerId}
                onChange={(e) => setNewOwnerId(e.target.value)}
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Find the user ID in the User Details dialog
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
              <Button onClick={handleToolTransfer} disabled={actionLoading || !newOwnerId} className="btn-primary">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Transfer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tool Status Change Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Change Tool Status</DialogTitle>
              <DialogDescription>
                Update status for <strong>{selectedTool?.manufacturer} {selectedTool?.tool_type}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm text-muted-foreground mb-2 block">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Stolen">Stolen</SelectItem>
                  <SelectItem value="Sold">Sold</SelectItem>
                  <SelectItem value="Discarded">Discarded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
              <Button onClick={handleToolStatusChange} disabled={actionLoading || !newStatus} className="btn-primary">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tool Delete Dialog */}
        <Dialog open={showToolDeleteDialog} onOpenChange={setShowToolDeleteDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-destructive">Delete Tool</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{selectedTool?.manufacturer} {selectedTool?.tool_type}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowToolDeleteDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleToolDelete} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Delete Tool
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
